import React from "react";
import { ActivityIndicator, Pressable, Text, View } from "react-native";

import type {
  TCommunicationsAssignedPhoneNumberItem,
  TCommunicationsPhoneNumberInventoryItem,
} from "../../../../../services/api-communications";
import { ADMIN_TOKENS } from "../../../../../styles/adminTokens";
import { PhoneNumberText, SearchInput } from "../../../../common/native";
import { styles } from "../HomeConversations.styles";
import { formatPhoneNumberDisplay } from "../../../../../utils/phoneNumber";

type PhoneNumbersCardProps = {
  isConfigTab: boolean;
  assignedNumbersLoading: boolean;
  availableNumbersLoading: boolean;
  assignedNumbersError: string | null;
  assignedPhoneNumbers: TCommunicationsAssignedPhoneNumberItem[];
  includedNumberCount: number;
  activeAssignedNumbersCount: number;
  extraAssignedNumbersCount: number;
  activationSubmitting: boolean;
  claimingPhoneNumberId: number | null;
  updatingPrimaryNumberId: number | null;
  unassigningPhoneNumberId: number | null;
  communicationsIsActivated: boolean;
  availableNumberSearchTerm: string;
  onAvailableNumberSearchTermChange: (value: string) => void;
  availableNumbersError: string | null;
  filteredAvailablePhoneNumbers: TCommunicationsPhoneNumberInventoryItem[];
  onSetPrimaryNumber: (
    phoneNumber: TCommunicationsAssignedPhoneNumberItem,
  ) => void;
  onUnassignNumber: (
    phoneNumber: TCommunicationsAssignedPhoneNumberItem,
  ) => void;
  onClaimAdditionalNumber: (
    phoneNumber: TCommunicationsPhoneNumberInventoryItem,
  ) => void;
  formatInteger: (value?: number | null) => string;
  formatPhoneCapabilities: (
    capabilities?: string[] | Record<string, boolean> | null,
  ) => string;
};

const PhoneNumbersCard: React.FC<PhoneNumbersCardProps> = ({
  isConfigTab,
  assignedNumbersLoading,
  availableNumbersLoading,
  assignedNumbersError,
  assignedPhoneNumbers,
  includedNumberCount,
  activeAssignedNumbersCount,
  extraAssignedNumbersCount,
  activationSubmitting,
  claimingPhoneNumberId,
  updatingPrimaryNumberId,
  unassigningPhoneNumberId,
  communicationsIsActivated,
  availableNumberSearchTerm,
  onAvailableNumberSearchTermChange,
  availableNumbersError,
  filteredAvailablePhoneNumbers,
  onSetPrimaryNumber,
  onUnassignNumber,
  onClaimAdditionalNumber,
  formatInteger,
  formatPhoneCapabilities,
}) => {
  return (
    <View style={[styles.card, !isConfigTab && styles.hiddenSection]}>
      <View style={styles.rowBetween}>
        <Text style={styles.cardTitle}>Phone Numbers</Text>
        {(assignedNumbersLoading || availableNumbersLoading) && (
          <ActivityIndicator size="small" color={ADMIN_TOKENS.color.accent} />
        )}
      </View>

      <View style={styles.summaryChipsRow}>
        <View style={styles.summaryChip}>
          <Text style={styles.summaryChipText}>
            Included:{" "}
            {formatInteger(
              Math.min(activeAssignedNumbersCount, includedNumberCount),
            )}
            /{formatInteger(includedNumberCount)}
          </Text>
        </View>
        <View style={styles.summaryChip}>
          <Text style={styles.summaryChipText}>
            Extra Numbers: {formatInteger(extraAssignedNumbersCount)}
          </Text>
        </View>
      </View>

      {assignedNumbersError ? (
        <Text style={styles.inlineErrorText}>{assignedNumbersError}</Text>
      ) : null}

      {assignedNumbersLoading ? (
        <Text style={styles.metaText}>Loading assigned numbers...</Text>
      ) : !assignedPhoneNumbers.length ? (
        <Text style={styles.emptyText}>
          No assigned numbers yet. Activate communications to assign your first
          number.
        </Text>
      ) : (
        <View style={styles.listContainer}>
          {assignedPhoneNumbers.map((numberItem, index) => {
            const isPrimary = numberItem.is_primary === true;
            const isIncluded = index < includedNumberCount;
            const actionBusy =
              activationSubmitting ||
              claimingPhoneNumberId !== null ||
              updatingPrimaryNumberId === numberItem.phone_number_id ||
              unassigningPhoneNumberId === numberItem.phone_number_id;

            return (
              <View key={numberItem.phone_number_id} style={styles.callRow}>
                <View style={styles.rowBetween}>
                  <PhoneNumberText
                    value={numberItem.e164}
                    style={styles.callNumberText}
                  />
                  <View
                    style={[
                      styles.statusBadge,
                      isPrimary
                        ? styles.primaryBadge
                        : styles.secondaryStatusBadge,
                    ]}
                  >
                    <Text
                      style={[
                        styles.statusBadgeText,
                        isPrimary
                          ? styles.primaryBadgeText
                          : styles.secondaryStatusBadgeText,
                      ]}
                    >
                      {isPrimary ? "Primary" : "Secondary"}
                    </Text>
                  </View>
                </View>
                <Text style={styles.metaText}>
                  {numberItem.friendly_name || "No friendly name"}
                </Text>
                <Text style={styles.metaText}>
                  Capabilities:{" "}
                  {formatPhoneCapabilities(numberItem.capabilities)}
                </Text>
                <Text style={styles.metaText}>
                  Billing: {isIncluded ? "Included" : "Extra Number"}
                </Text>
                <View style={styles.callRowActions}>
                  <Pressable
                    style={[
                      styles.callAgainButton,
                      (isPrimary || actionBusy) &&
                        styles.callAgainButtonDisabled,
                    ]}
                    onPress={() => onSetPrimaryNumber(numberItem)}
                    disabled={isPrimary || actionBusy}
                    accessibilityLabel={`Set ${formatPhoneNumberDisplay(numberItem.e164, { fallback: "number" })} as primary`}
                  >
                    <Text style={styles.callAgainButtonText}>
                      {updatingPrimaryNumberId === numberItem.phone_number_id
                        ? "Saving..."
                        : isPrimary
                          ? "Primary"
                          : "Set Primary"}
                    </Text>
                  </Pressable>
                  <Pressable
                    style={[
                      styles.assignModalSecondaryButton,
                      actionBusy && styles.callAgainButtonDisabled,
                    ]}
                    onPress={() => onUnassignNumber(numberItem)}
                    disabled={actionBusy}
                    accessibilityLabel={`Unassign ${formatPhoneNumberDisplay(numberItem.e164, { fallback: "number" })}`}
                  >
                    <Text style={styles.assignModalSecondaryButtonText}>
                      {unassigningPhoneNumberId === numberItem.phone_number_id
                        ? "Unassigning..."
                        : "Unassign"}
                    </Text>
                  </Pressable>
                </View>
              </View>
            );
          })}
        </View>
      )}

      <Text style={styles.inputLabel}>Claim Additional Numbers</Text>
      {!communicationsIsActivated ? (
        <Text style={styles.metaText}>
          Activate communications first to claim additional inventory.
        </Text>
      ) : (
        <>
          <SearchInput
            value={availableNumberSearchTerm}
            onChangeText={onAvailableNumberSearchTermChange}
            onClear={() => onAvailableNumberSearchTermChange("")}
            loading={availableNumbersLoading}
            placeholder="Search available numbers..."
            placeholderTextColor={ADMIN_TOKENS.color.textMuted}
            containerStyle={styles.patientSearchInputContainer}
            inputStyle={styles.patientSearchInput}
            iconColor={ADMIN_TOKENS.color.textMuted}
          />
          {availableNumbersError ? (
            <Text style={styles.inlineErrorText}>{availableNumbersError}</Text>
          ) : availableNumbersLoading ? (
            <Text style={styles.metaText}>Loading number inventory...</Text>
          ) : !filteredAvailablePhoneNumbers.length ? (
            <Text style={styles.emptyText}>
              No available inventory matches your search.
            </Text>
          ) : (
            <View style={styles.listContainer}>
              {filteredAvailablePhoneNumbers.slice(0, 12).map((numberItem) => {
                const claimBusy =
                  claimingPhoneNumberId !== null || activationSubmitting;

                return (
                  <View
                    key={`available-${numberItem.phone_number_id}`}
                    style={styles.recordingRow}
                  >
                    <PhoneNumberText
                      value={numberItem.e164}
                      style={styles.callNumberText}
                    />
                    <Text style={styles.metaText}>
                      {numberItem.friendly_name || "No friendly name"}
                    </Text>
                    <Text style={styles.metaText}>
                      Capabilities:{" "}
                      {formatPhoneCapabilities(numberItem.capabilities)}
                    </Text>
                    <Pressable
                      style={[
                        styles.callAgainButton,
                        claimBusy && styles.callAgainButtonDisabled,
                      ]}
                      onPress={() => onClaimAdditionalNumber(numberItem)}
                      disabled={claimBusy}
                      accessibilityLabel={`Claim ${formatPhoneNumberDisplay(numberItem.e164, { fallback: "number" })}`}
                    >
                      <Text style={styles.callAgainButtonText}>
                        {claimingPhoneNumberId === numberItem.phone_number_id
                          ? "Claiming..."
                          : "Claim Number"}
                      </Text>
                    </Pressable>
                  </View>
                );
              })}
            </View>
          )}
        </>
      )}
    </View>
  );
};

export default PhoneNumbersCard;
